import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Loader2, Upload, X, CheckCircle2, AlertCircle, Circle, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { listImports } from "@/lib/contacts.functions";
import { processImport, createPendingImport, markImportFailed } from "@/lib/imports.functions";
import { supabase } from "@/integrations/supabase/client";
import { getWorkspaceById } from "@/lib/dashboard.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/imports")({
  component: ImportsView,
});

type ReportTypeChoice = "auto" | "commissions" | "active_users" | "trials" | "second_tier";

type QueueStatus = "pending" | "uploading" | "processing" | "done" | "failed";

type QueueItem = {
  id: string;
  file: File;
  reportChoice: ReportTypeChoice;
  status: QueueStatus;
  message?: string;
};

function guessFromFilename(name: string): ReportTypeChoice {
  const n = name.toLowerCase();
  if (/trials?/.test(n)) return "trials";
  if (/2nd[\s_-]?tier|second[\s_-]?tier|tier[\s_-]?2/.test(n)) return "second_tier";
  if (/active[\s_-]*(user|subscriber|member|affiliate)|paid[\s_-]*user|subscriber/.test(n)) return "active_users";
  if (/commission|sales?|orders?|transactions?/.test(n)) return "commissions";
  return "auto";
}

function ImportsView() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/imports" });
  const fetchImports = useServerFn(listImports);
  const fetchWs = useServerFn(getWorkspaceById);
  const process = useServerFn(processImport);
  const createPending = useServerFn(createPendingImport);
  const markFailed = useServerFn(markImportFailed);
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState<QueueItem[]>([]);

  const { data: wsData } = useQuery({ queryKey: ["workspace", workspaceId], queryFn: () => fetchWs({ data: { workspace_id: workspaceId } }) });
  const { data, isLoading } = useQuery({ queryKey: ["imports", workspaceId], queryFn: () => fetchImports({ data: { workspace_id: workspaceId } }) });
  const accountId = wsData?.workspace?.account_id;

  const onFilesPicked = (files: File[]) => {
    const items: QueueItem[] = files.map((file) => ({
      id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      reportChoice: guessFromFilename(file.name),
      status: "pending",
    }));
    setQueue((prev) => [...prev, ...items]);
  };

  const updateItem = (id: string, patch: Partial<QueueItem>) => {
    setQueue((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const removeItem = (id: string) => {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const isTransientError = (msg: string) => {
    const m = msg.toLowerCase();
    return (
      m.includes("upstream request timeout") ||
      m.includes("gateway timeout") ||
      m.includes("524") ||
      m.includes("522") ||
      m.includes("network error") ||
      m.includes("failed to fetch") ||
      m.includes("econnreset") ||
      m.includes("timed out")
    );
  };

  const processOne = async (item: QueueItem, opts?: { retryAttempt?: number }) => {
    if (!accountId) return;
    const { file, reportChoice } = item;
    const retryAttempt = opts?.retryAttempt ?? 0;

    let importId: string | null = null;
    try {
      const pending = await createPending({
        data: { workspace_id: workspaceId, source_filename: file.name },
      });
      importId = pending.import_id;
    } catch (e: any) {
      updateItem(item.id, { status: "failed", message: `Could not record import: ${e?.message ?? "unknown"}` });
      return;
    }

    updateItem(item.id, { status: "uploading", message: undefined });
    const path = `${accountId}/${workspaceId}/${Date.now()}-${file.name}`;

    try {
      const { error } = await supabase.storage.from("csv-imports").upload(path, file);
      if (error) throw error;
    } catch (e: any) {
      const msg = e?.message ?? "Storage upload failed";
      await markFailed({
        data: { import_id: importId, workspace_id: workspaceId, stage: "storage_upload", error: msg },
      }).catch(() => {});
      updateItem(item.id, { status: "failed", message: msg });
      return;
    }

    updateItem(item.id, {
      status: "processing",
      message: retryAttempt > 0 ? `Retrying (attempt ${retryAttempt + 1})…` : undefined,
    });
    try {
      const res = await process({
        data: {
          workspace_id: workspaceId,
          storage_path: path,
          source_filename: file.name,
          report_type_override: reportChoice === "auto" ? undefined : reportChoice,
          existing_import_id: importId,
        },
      });

      if ((res as any).skipped) {
        updateItem(item.id, { status: "done", message: (res as any).message ?? "Skipped duplicate" });
      } else if (res.needs_mapping) {
        updateItem(item.id, { status: "done", message: `${res.unmapped_statuses?.length ?? 0} status values need mapping` });
      } else if (res.report_type === "commissions") {
        const committed = (res as any).transactions_committed ?? 0;
        if (committed === 0) {
          updateItem(item.id, { status: "failed", message: "Processed but 0 transactions committed" });
        } else {
          const ins = (res as any).rows_inserted ?? 0;
          const upd = (res as any).rows_updated ?? 0;
          updateItem(item.id, { status: "done", message: `${committed} rows · ${ins} new · ${upd} updated` });
        }
      } else {
        updateItem(item.id, { status: "done", message: `${res.added ?? 0} added · ${res.updated ?? 0} updated` });
      }
    } catch (e: any) {
      const msg = e?.message ?? "Processing failed";

      // Auto-retry once on transient gateway timeouts. The server likely kept
      // processing in the background; we wait a bit, then trigger a fresh
      // attempt against a new import row. The duplicate-file check on the
      // server will skip the second attempt if the first actually finished.
      if (isTransientError(msg) && retryAttempt === 0) {
        updateItem(item.id, {
          status: "processing",
          message: "Gateway timed out — auto-retrying in 15s…",
        });
        await markFailed({
          data: { import_id: importId, workspace_id: workspaceId, stage: "process", error: `${msg} (auto-retrying)` },
        }).catch(() => {});
        await new Promise((r) => setTimeout(r, 15000));
        return processOne(item, { retryAttempt: 1 });
      }

      const friendlyMsg = isTransientError(msg)
        ? `${msg} — large file; server may still be processing in the background. Check the imports list in ~1 min, then click Retry only if it didn't land.`
        : msg;
      await markFailed({
        data: { import_id: importId, workspace_id: workspaceId, stage: "process", error: msg },
      }).catch(() => {});
      updateItem(item.id, { status: "failed", message: friendlyMsg });
    }
  };

  const retryOne = async (id: string) => {
    const item = queue.find((q) => q.id === id);
    if (!item) return;
    setUploading(true);
    await processOne({ ...item, status: "pending", message: undefined });
    qc.invalidateQueries({ queryKey: ["imports", workspaceId] });
    qc.invalidateQueries({ queryKey: ["dashboard", workspaceId] });
    qc.invalidateQueries({ queryKey: ["unclassified", workspaceId] });
    setUploading(false);
  };

  const uploadAll = async () => {
    if (!accountId) return;
    setUploading(true);
    // Process sequentially — back-to-back reconciliation safely handled by the
    // per-workspace push settle delay; sequential avoids contact-upsert races.
    // Snapshot the queue at click time and walk it; status updates flow via setQueue.
    const snapshot = queue.filter((q) => q.status === "pending" || q.status === "failed");
    for (const item of snapshot) {
      await processOne({ ...item, status: "pending" });
    }
    qc.invalidateQueries({ queryKey: ["imports", workspaceId] });
    qc.invalidateQueries({ queryKey: ["dashboard", workspaceId] });
    qc.invalidateQueries({ queryKey: ["unclassified", workspaceId] });
    setUploading(false);

    // Summarise
    const final = (await new Promise<QueueItem[]>((resolve) => {
      setQueue((prev) => { resolve(prev); return prev; });
    }));
    const done = final.filter((q) => q.status === "done").length;
    const failed = final.filter((q) => q.status === "failed").length;
    if (failed === 0 && done > 0) toast.success(`${done} file${done === 1 ? "" : "s"} processed`);
    else if (done > 0) toast.warning(`${done} succeeded · ${failed} failed`);
    else if (failed > 0) toast.error(`${failed} file${failed === 1 ? "" : "s"} failed`);
  };

  const clearCompleted = () => {
    setQueue((prev) => prev.filter((q) => q.status !== "done"));
  };

  const hasPending = queue.some((q) => q.status === "pending" || q.status === "failed");

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="font-medium">Upload fresh exports</div>
            <div className="text-sm text-muted-foreground">
              CSV. Drop one or many — we'll confirm what each report is before processing.
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length) onFilesPicked(files);
              e.target.value = "";
            }}
          />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Upload CSVs
          </Button>
        </CardContent>
      </Card>

      <Dialog open={queue.length > 0} onOpenChange={(open) => { if (!open && !uploading) setQueue([]); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm report types ({queue.length} file{queue.length === 1 ? "" : "s"})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {queue.map((item) => (
              <div key={item.id} className="flex items-center gap-2 rounded-md border p-2">
                <div className="shrink-0">
                  {item.status === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  {item.status === "failed" && <AlertCircle className="h-4 w-4 text-destructive" />}
                  {(item.status === "uploading" || item.status === "processing") && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {item.status === "pending" && <Circle className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs truncate">{item.file.name}</div>
                  {item.message && (
                    <div className={`text-xs mt-0.5 ${item.status === "failed" ? "text-destructive" : "text-muted-foreground"}`}>
                      {item.status === "uploading" ? "Uploading…" : item.status === "processing" ? "Processing…" : item.message}
                    </div>
                  )}
                  {!item.message && (item.status === "uploading" || item.status === "processing") && (
                    <div className="text-xs mt-0.5 text-muted-foreground">
                      {item.status === "uploading" ? "Uploading…" : "Processing…"}
                    </div>
                  )}
                </div>
                <Select
                  value={item.reportChoice}
                  onValueChange={(v) => updateItem(item.id, { reportChoice: v as ReportTypeChoice })}
                  disabled={uploading || item.status === "done"}
                >
                  <SelectTrigger className="w-[170px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commissions">Commissions / Sales</SelectItem>
                    <SelectItem value="active_users">Active Users</SelectItem>
                    <SelectItem value="trials">Trials</SelectItem>
                    <SelectItem value="second_tier">2nd Tier Affiliates</SelectItem>
                    <SelectItem value="auto">Auto-detect</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => retryOne(item.id)}
                  disabled={uploading || item.status !== "failed"}
                  title="Retry"
                >
                  <RotateCcw className={`h-4 w-4 ${item.status === "failed" ? "" : "opacity-30"}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => removeItem(item.id)}
                  disabled={uploading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Files process one at a time to keep reconciliation safe. CRM pushes are buffered by your push settle delay so the system sees the full picture before firing workflows.
          </p>
          <DialogFooter>
            {queue.some((q) => q.status === "done") && (
              <Button variant="outline" onClick={clearCompleted} disabled={uploading}>Clear completed</Button>
            )}
            <Button variant="ghost" onClick={() => setQueue([])} disabled={uploading}>Close</Button>
            <Button onClick={uploadAll} disabled={uploading || !hasPending}>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload & process all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.imports ?? []).map((i: any) => (
                <TableRow key={i.id}>
                  <TableCell className="font-mono text-xs">{i.source_filename}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{i.report_type ?? ","}</Badge></TableCell>
                  <TableCell>
                    <Badge variant={
                      i.status === "processed" ? "default"
                      : i.status === "failed" ? "destructive"
                      : i.status === "skipped_duplicate" ? "outline"
                      : "secondary"
                    }>
                      {i.status === "skipped_duplicate" ? "skipped (duplicate)" : i.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{i.row_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md">
                    {i.status === "failed" ? (
                      <span className="text-destructive">
                        {i.error_message ?? i.processing_summary_json?.error ?? "Unknown failure"}
                        {i.processing_summary_json?.stage ? ` (${i.processing_summary_json.stage})` : ""}
                      </span>
                    ) : i.status === "skipped_duplicate" ? (
                      <span>{i.error_message ?? "Identical file already processed, skipped."}</span>
                    ) : (i.rows_inserted != null || i.rows_updated != null || i.rows_unchanged != null) ? (
                      `${i.rows_inserted ?? 0} new · ${i.rows_updated ?? 0} updated · ${i.rows_unchanged ?? 0} unchanged`
                    ) : i.processing_summary_json?.transactions_committed != null ? (
                      `${i.processing_summary_json.transactions_committed} committed${i.processing_summary_json.transactions_failed ? ` · ${i.processing_summary_json.transactions_failed} failed` : ""} · +${i.processing_summary_json.added ?? 0} / ~${i.processing_summary_json.updated ?? 0}`
                    ) : i.processing_summary_json?.added != null
                      ? `+${i.processing_summary_json.added} / ~${i.processing_summary_json.updated}`
                      : i.processing_summary_json?.unmapped_statuses?.length
                        ? `${i.processing_summary_json.unmapped_statuses.length} unmapped`
                        : ","}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(i.uploaded_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(data?.imports ?? []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No imports yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
